<script lang="ts">
  import { api } from '../../lib/api/client';
  import PeriodPicker from './PeriodPicker.svelte';
  import type { DaysIn, RegionSummary } from '../../lib/api/types';
  import { t } from '../../lib/i18n/index.ts';
  import { countryName, shortDate, span } from './format';

  // Regions lists the states and provinces visited, with the places in each
  // and the first and last visit (spec 005 FR-022), and a year's days in
  // each country and region, a day counting for every one visited that day
  // (005 FR-023).
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const thisYear = new Date().getFullYear();
  let sort = $state('visits');
  // period is the from and to of the chosen stretch, empty for all time.
  let period = $state('');
  let regions = $state<RegionSummary[] | null>(null);
  let year = $state(thisYear);
  let daysIn = $state<DaysIn | null>(null);
  let error = $state('');

  $effect(() => {
    const q = `tz=${encodeURIComponent(tz)}&sort=${sort}${period}`;
    api.get<RegionSummary[]>(`/me/timeline/regions?${q}`)
      .then((got) => { regions = got; error = ''; })
      .catch((e) => { error = e.message; regions = null; });
  });

  $effect(() => {
    const y = year;
    api.get<DaysIn>(`/me/timeline/days-in?year=${y}&tz=${encodeURIComponent(tz)}`)
      .then((got) => { if (got.year === year) daysIn = got; })
      .catch((e) => (error = e.message));
  });

  const days = (n: number) => t('timelineDayCount', { n });
  const regionName = (r: string) => r || t('timelineUnknownRegion');
  const known = $derived((regions ?? []).filter((r) => r.region).length);
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    <section class="card p-5" aria-labelledby="days-in-heading">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 id="days-in-heading" class="font-semibold">{t('timelineDaysPerRegion')}</h2>
        <div class="flex items-center gap-1 text-sm">
          <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearBefore')} onclick={() => year--}><i aria-hidden="true" class="las la-chevron-left"></i></button>
          <span class="px-1 font-medium" aria-live="polite">{year}</span>
          <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearAfter')} disabled={year >= thisYear} onclick={() => year++}><i aria-hidden="true" class="las la-chevron-right"></i></button>
        </div>
      </div>
      {#if daysIn && daysIn.year === year}
        {#if daysIn.countries.length === 0}
          <p class="text-sm text-text-muted">{t('timelineNoCountryIn', { year: String(year) })}</p>
        {:else}
          <div class="grid sm:grid-cols-2 gap-x-6">
            <div>
              <h3 class="text-xs text-text-muted mt-2">{t('timelineCountries')}</h3>
              <ul>
                {#each daysIn.countries as c (c.country_code)}
                  <li class="py-1.5 flex items-baseline gap-3"><span class="flex-1">{countryName(c.country_code, c.country)}</span><span class="text-sm tabular-nums">{days(c.days)}</span></li>
                {/each}
              </ul>
            </div>
            {#if daysIn.regions.length}
              <div>
                <h3 class="text-xs text-text-muted mt-2">{t('timelineRegions')}</h3>
                <ul>
                  {#each daysIn.regions as r (`${r.country_code}/${r.region}`)}
                    <li class="py-1.5 flex items-baseline gap-3">
                      <span class="flex-1 min-w-0 truncate">{r.region}<span class="text-xs text-text-muted">, {countryName(r.country_code, r.country)}</span></span>
                      <span class="text-sm tabular-nums">{days(r.days)}</span>
                    </li>
                  {/each}
                </ul>
              </div>
            {/if}
          </div>
          <p class="text-xs text-text-muted mt-2">{t('timelineDayCounts')}</p>
        {/if}
      {/if}
    </section>

    <div class="flex items-center gap-3 flex-wrap">
      <PeriodPicker bind:query={period} />
      <select class="input w-48" bind:value={sort} aria-label={t('timelineSort')}>
        <option value="visits">{t('timelineSortVisits')}</option>
        <option value="time">{t('timelineSortTime')}</option>
        <option value="recent">{t('timelineSortRecent')}</option>
      </select>
    </div>

    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if regions}
      <section class="card p-5">
        <h2 class="font-semibold mb-2">{t('timelineRegionCount', { n: known })}</h2>
        {#if regions.length === 0}
          <p class="text-sm text-text-muted">{t('timelineNoStays')}</p>
        {/if}
        {#each regions as r (`${r.country_code}/${r.region}`)}
          <div class="py-2 flex items-baseline gap-3">
            <span class="flex-1 min-w-0">
              <span class="block font-medium truncate {r.region ? '' : 'text-text-muted'}">{regionName(r.region)}</span>
              <span class="block text-xs text-text-muted">{countryName(r.country_code, r.country)}</span>
            </span>
            <span class="text-sm text-text-muted text-right">
              {t('timelineRegionStats', { places: r.places, visits: r.visits, time: span(r.seconds) })}<br />{t('timelineDateRange', { start: shortDate(r.first_visit), end: shortDate(r.last_visit) })}
            </span>
          </div>
        {/each}
      </section>
    {/if}
  </div>
</div>
